import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, FlexBox, Title, Icon, Text, BusyIndicator } from '@ui5/webcomponents-react';
import { useAuth } from '../store/auth';
import { useIsMobile } from '../hooks/useIsMobile';
import { api, type Kpi } from '../api/client';

const TONE: Record<Kpi['tone'], string> = {
  info: 'var(--sapInformativeColor, #0a6ed1)',
  good: 'var(--sapPositiveColor, #107e3e)',
  warn: 'var(--sapNegativeColor, #bb0000)',
  neutral: 'var(--sapNeutralColor, #6a6d70)',
};

export function HomePage() {
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const { principal, can } = useAuth();
  const screens = useQuery({ queryKey: ['screens'], queryFn: api.screens, staleTime: 60_000 });
  // One cached call to the server instead of N live counts; server keeps values warm (stale-while-revalidate).
  const kpi = useQuery({ queryKey: ['kpi'], queryFn: api.kpis, staleTime: 60_000, refetchInterval: 120_000 });
  const tiles = kpi.data ?? [];

  const launchpad = (screens.data ?? []).filter(s => s.menu?.showOnHome && (s.access || can(`${s.entity}.View`)));

  return (
    <div style={{ paddingBottom: '2rem' }}>
      <Title level="H3" style={{ margin: '0.5rem 0 1rem' }}>Welcome, {principal?.displayName}</Title>

      {(tiles.length > 0 || kpi.isLoading) && (
        <>
          <Title level="H5" style={{ margin: '0 0 0.5rem', color: 'var(--sapNeutralTextColor)' }}>Overview</Title>
          {kpi.isLoading ? <BusyIndicator active /> : (
            <FlexBox wrap="Wrap" style={{ gap: '0.75rem', marginBottom: '1.5rem' }}>
              {tiles.map(k => (
                <div key={k.id} onClick={() => k.screen && nav(`/s/${k.screen}`)}
                  style={{ width: isMobile ? 'calc(50% - 0.375rem)' : '12rem', boxSizing: 'border-box', cursor: k.screen ? 'pointer' : 'default', background: 'var(--sapTile_Background, #fff)',
                    borderRadius: '0.75rem', padding: '0.9rem 1rem', boxShadow: 'var(--sapContent_Shadow0, 0 0 0 1px rgba(0,0,0,0.07))',
                    borderLeft: `4px solid ${TONE[k.tone]}` }}>
                  <FlexBox justifyContent="SpaceBetween" alignItems="Center">
                    <Text style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.1, color: TONE[k.tone] }}>{k.value.toLocaleString()}</Text>
                    <Icon name={k.icon} style={{ color: TONE[k.tone], opacity: 0.5 }} />
                  </FlexBox>
                  <Text style={{ fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.25rem', display: 'block' }}>{k.label}</Text>
                </div>
              ))}
            </FlexBox>
          )}
        </>
      )}

      <Title level="H5" style={{ margin: '0 0 0.5rem', color: 'var(--sapNeutralTextColor)' }}>Quick access</Title>
      <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
        {launchpad.map(t => (
          <Card key={t.id} onClick={() => nav(`/s/${t.id}`)} style={{ width: isMobile ? '100%' : '13rem', cursor: 'pointer' }}
            header={<CardHeader titleText={t.title} subtitleText={t.menu?.group} interactive avatar={<Icon name={t.icon ?? 'document'} />} />}>
            <div style={{ height: '2rem' }} />
          </Card>
        ))}
      </FlexBox>
      {screens.isSuccess && launchpad.length === 0 && tiles.length === 0 && <Text>No screens assigned to your role yet.</Text>}
    </div>
  );
}
